import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  useTheme,
} from "@mui/material";
import {
  useNavigate,
  Link as RouterLink,
  useSearchParams,
} from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useAuth } from "../../context/AuthContext";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import Logo from "../../components/Logo";
import { useBranding } from "../../context/BrandingContext";

interface IFormInput {
  password: string;
  password_confirmation: string;
}

const schema = yup.object({
  password: yup
    .string()
    .min(6, "Senha deve ter pelo menos 6 caracteres")
    .required("Senha é obrigatória"),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref("password")], "As senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
});

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { resetPassword } = useAuth();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { branding } = useBranding();
  const backgroundGradient = `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`;
  const borderGradient = `linear-gradient(to top, ${branding.colors.primary}, rgba(0, 0, 0, 0)) 1 100%`;

  // Atualiza o título e favicon da página
  useDocumentMeta();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: IFormInput) => {
    if (!token) {
      setError("Token inválido");
      return;
    }

    try {
      setLoading(true);
      setError("");
      await resetPassword(token, data.password, data.password_confirmation);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Erro ao resetar senha");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: backgroundGradient,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            height: "100vh",
            position: "relative",
            justifyContent: "center",
            borderWidth: "3px",
            borderStyle: "solid",
            borderColor: "initial",
            borderRadius: "11px",
            borderImage: borderGradient,
            sm: { width: "90%" },
            md: {
              width: "40%",
            },
            lg: {
              width: "35%",
            },
            xl: {
              width: "30%",
            },
          }}
        >
          <CardContent
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
              <Logo height={48} />
            </Box>
            <Alert severity="error">
              Token inválido ou expirado. Solicite uma nova recuperação de
              senha.
            </Alert>
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Link component={RouterLink} to="/forgot-password">
                Solicitar nova recuperação
              </Link>
            </Box>
          </CardContent>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: backgroundGradient,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          position: "relative",
          justifyContent: "center",
          borderWidth: "3px",
          borderStyle: "solid",
          borderColor: "initial",
          borderRadius: "11px",
          borderImage: borderGradient,
          sm: { width: "90%" },
          md: {
            width: "40%",
          },
          lg: {
            width: "35%",
          },
          xl: {
            width: "30%",
          },
        }}
      >
        <CardContent
          sx={{
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
            <Logo height={48} />
          </Box>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            fontWeight="bold"
            color={theme.palette.common.white}
          >
            Nova Senha
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color={theme.palette.common.white}
            sx={{ mb: 3 }}
          >
            Digite sua nova senha
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Senha alterada com sucesso! Redirecionando para o login...
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              fullWidth
              label="Nova senha"
              type="password"
              margin="normal"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                sx: {
                  color: branding.colors.textDark,
                  backgroundColor: branding.colors.background,
                  borderRadius: "11px",
                  border: `1px solid ${branding.colors.primary}`,
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirmar nova senha"
              type="password"
              margin="normal"
              {...register("password_confirmation")}
              error={!!errors.password_confirmation}
              helperText={errors.password_confirmation?.message}
              InputProps={{
                sx: {
                  color: branding.colors.textDark,
                  backgroundColor: branding.colors.background,
                  borderRadius: "11px",
                  border: `1px solid ${branding.colors.primary}`,
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              size="large"
              disabled={loading || success}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : "Alterar senha"}
            </Button>
          </form>

          <Box sx={{ textAlign: "center" }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Voltar para o login
            </Link>
          </Box>
        </CardContent>
      </Box>
    </Box>
  );
};

export default ResetPassword;
